import React, { useState } from 'react'
import Datepicker from '../../../components/common/DatePicker/Datepicker'
import { ApproverRequestDetails } from '../../../store/slices/Approver/approverRequestDetailsSlice'
import { formatDateToMMDDYYYY } from '../../../utils/date'
import {buildCurrentRequestTooltipDetails} from "../../../utils/buildCurrentRequestDetailsTooltip"
import DetailsTable from './DetailsTable'
import plusIcon from '../../../../public/assets/images/plus-circle-outline.svg'
import minusIcon from '../../../../public/assets/images/minus-circle-outline.svg'
import errorIcon from  "../../../../public/assets/images/errorCircleSmall.svg"
import { getEnvironmentConfig } from '../../../utils/detectEnv'

interface RequestInfoProps {
  data: ApproverRequestDetails | null
  approvedHoursById?: Record<string, number>
}
  const envConfig = getEnvironmentConfig() as any
  const configuredHrConnectUrl = envConfig.HRConnectUrl || ''
/**
 * RequestInfo component — displays Current Request, Request Details,
 * and Other Requests sections for a time off request.
 *
 * Dynamically builds all sections from approver request details returned by the API.
 * User's browser timezone is automatically detected and passed to the API.
 */
const RequestInfo: React.FC<RequestInfoProps> = ({ data, approvedHoursById }) => {
  const [expandEmployeeDetails, setExpandEmployeeDetails] = useState(false)

  // ✅ Move hook and dependencies BEFORE early return (Rules of Hooks)
  const highlightedDates = data?.requestedDates?.map(d => d.date) ?? []
  const formattedHighlightedDates = highlightedDates.map(formatDateToMMDDYYYY)
const currentRequestTooltipDetails =
  buildCurrentRequestTooltipDetails(
    Object.fromEntries(
      (data?.requestedDates ?? []).map(item => [
        item.date,
        {
          toReqId: data?.toReqId ?? '',
          toReqDtlId: item.toReqDtlId ?? 0,

          // IMPORTANT
          durationFlag:
            item.durationFlag ??
            (item.startTime && item.endTime ? 'P' : 'F'),

          day: item.day ?? item.date,

          startTime: item.startTime ?? null,
          endTime: item.endTime ?? null,

          timeOffAllocations:
            item.timeOffAllocations ?? [],
        }
      ])
    )
  )
  if (!data) {
    return <div className="text-center py-12">No request details available.</div>
  }
const NO_EMAIL_MSG =
  'There is no email selected or on file. Email notifications will not be sent.'

const getPreferredEmail = (data: any) => {
  const emails: string[] = []
  if (data?.kpSelected && data?.kpEmail) {
    emails.push(data.kpEmail)
  }
  if (data?.alternateSelected && data?.alternateEmail) {
    emails.push(data.alternateEmail)
  }

  if (emails.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <img src={errorIcon} alt="error icon" />
        <span className="text-red-600 p3 -book">
          {NO_EMAIL_MSG}
        </span>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {emails.map((email) => (
        <p
          className="p3 -book mb-0! mt-0!"
          key={email}
        >
          {email}
        </p>
      ))}
    </div>
  )
}
  // Calendar highlighted dates (already calculated above)
  // const highlightedDates = (data.requestedDates ?? []).map(d => d.date)

  // Format dates to MM/DD/YYYY for the hook (already calculated above)
  // const formattedHighlightedDates = highlightedDates.map(formatDateToMMDDYYYY)

  // Fetch other request details (pre-populated leaves) (already called above)
  // const { otherRequestDates, otherRequestDetailsForTooltip, handleMonthYearChange } = useOtherRequestDetails(formattedHighlightedDates)

  // Extract employee details from Redux data
  const getEmployeeDetailsFromAudit = () => {
    const firstStatus = data.statuses?.[0]

    return {
      employeeName: data.employee?.employeeName || 'N/A',
      nuid: data.empNUID || 'N/A',
      email: data.employee?.kpEmail || 'N/A',
      approver: data.employee?.primaryApprover || firstStatus?.fullName || 'Not assigned',
      job: data.employee?.job || 'N/A',
      union: data.employee?.union || 'N/A',
      department: data.employee?.department || 'N/A',
      location: data.employee?.location || 'N/A',
      region: data.employee?.regionName || 'N/A',
      shift: data.employee?.shift || 'N/A',
      unionSeniority: data.employee?.unionSeniorityDate || 'NA',
      alternateSeniority: data.employee?.alternateSeniorityDate || 'NA',
    }
  }

  const employeeDetails = getEmployeeDetailsFromAudit()

  // Build top-level employee details array (static section)
  const topLevelDetails = [
    { id: 'employee-nuid', label: 'Employee (NUID)', value: `${data.employee?.employeeName || 'N/A'}` },
    { id: 'preferred-email', label: 'Preferred e-mail', value: getPreferredEmail(data?.employee) },
    { id: 'authorized-approver', label: 'Primary Approver', value: employeeDetails.approver },
  ]

  // Build collapsible employee details array (expandable section)
  const expandableDetails = [
    { id: 'job', label: 'Job', value: employeeDetails.job },
    { id: 'union', label: 'Union', value: employeeDetails.union },
    { id: 'union-seniority', label: 'Union Seniority Date', value: employeeDetails.unionSeniority },
    { id: 'alternate-seniority', label: 'Alternate Seniority Date', value: employeeDetails.alternateSeniority },
    { id: 'department', label: 'Department', value: employeeDetails.department },
    { id: 'location', label: 'Location', value: employeeDetails.location },
    { id: 'region', label: 'Region', value: employeeDetails.region },
    { id: 'shift', label: 'Shift', value: employeeDetails.shift },
  ]


 const validHrConnectUrl = configuredHrConnectUrl

  return (
    <div className="space-y-8">
      {/* Tables Section: Current Request & Request Details */}
      <DetailsTable
        requestedDates={data.requestedDates}
        balance={data.balance}
        upcomingApprovedHours={data.upcomingApprovedHours}
        data={data}
        approvedHoursById={approvedHoursById}
      />
      <div className="border-t border-[#D9E2EC]"></div>

      {/* Other Requests Section */}
      <div className="w-full mt-6 pt-6!">
        <h3 className="h3">Other Requests</h3>
        <div className="mt-6!">
          <p className="p3 -book">
            View request details by clicking a specific date in the request.
          </p>

          {/* Calendar and Employee Details */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mt-4 pt-6!">
            {/* Left: Calendar */}
            <div className="lg:col-span-1">
              <Datepicker
                openBehaviour="calendarView"
                value={formattedHighlightedDates}
                datesToBeHighlighted={formattedHighlightedDates}
                otherRequestDetails={currentRequestTooltipDetails}
                readOnly={true}
                name=""
                calendarWidth={350}
                calendarHeight={350}
              />
            </div>

            {/* Right: Employee Details */}
            <div className="pb-6! gap-1!">
              <h3 className="text-black text-[20px] font-light leading-7 tracking-[0.01px] mb-3">
                Employee Details
              </h3>

              <div className="space-y-4 mb-6 pb-6!">
                {topLevelDetails.map((item) => (
                  <div key={item.id} className="flex justify-between pb-2!">
                    <span className="text-[16px] whitespace-nowrap">
                      {item.label}: {item.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Expand Employee Details Button */}
              <button
                className="flex items-center gap-2 w-full px-3 py-2 hover:opacity-80 cursor-pointer mb-6"
                onClick={() => setExpandEmployeeDetails(!expandEmployeeDetails)}
              >
                <img
                  src={expandEmployeeDetails ? minusIcon : plusIcon}
                  alt={expandEmployeeDetails ? 'Collapse' : 'Expand'}
                  className="w-5 h-5"
                />
                <span className="p2 -medium text-[#003B71]">
                  {expandEmployeeDetails ? 'Hide' : 'Show'} Employee Details
                </span>
              </button>

              {!expandEmployeeDetails && (
                <div className="mt-6! pb-4!">
                  <hr className="-variation-1" />
                </div>
              )}

              {/* Expanded Employee Details */}
              {expandEmployeeDetails && (
                <div className="pt-6 space-y-3 pl-6! pr-2! pb-2! mt-6!">
                  {expandableDetails.map((item) => (
                    <div key={item.id} className="flex justify-between pb-3!">
                      <span className="text-[16px] whitespace-nowrap">
                        {item.label}: {item.value}
                      </span>
                    </div>
                  ))}

                  <div className="mt-4 pb-3! pt-4">
                    <p className="p3 -book text-gray-600 text-xs">
                      Employee information provided by{' '}
                      {validHrConnectUrl ? (
                        <a
                          href={validHrConnectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-[#0074AD]"
                        >
                          HRconnect
                        </a>
                      ) : (
                        <span className="text-[#0074AD]">HRconnect</span>
                      )}
                      . Contact your approver to correct any inaccurate
                      information.
                    </p>
                  </div>

                  <div className="mt-6! pb-4!">
                    <hr className="-variation-1" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RequestInfo
